import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useRef } from "react";

import { useChatChannel } from "../api/useChatChannel";
import { useChatMessages } from "../api/useChatMessages";
import { useEnsureChatChannel } from "../api/useEnsureChatChannel";
import { useSendFileMessage } from "../api/useSendFileMessage";
import { useSendTextMessage } from "../api/useSendTextMessage";
import { type Exclusion, useWorkerBlockedStatus } from "../api/useWorkerBlockedStatus";
import { useWorkerHasActiveChartingNoncompliance } from "../api/useWorkerHasActiveChartingNoncompliance";
import { type ReplyContext } from "../types";
import { logChatMessageSent } from "../utils/logChatEvents";
import { useReplyState } from "./useReplyState";

interface UseChatPanelStateParams {
  modalState: UseModalState;
  workplaceId?: string;
  workerId: string;
  placementId?: string;
  onClose?: () => void;
}

interface UseChatPanelStateResult {
  workplaceId: string;
  isAdmin: boolean;
  isLoading: boolean;
  isError: boolean;
  isBlocked: boolean;
  isSendingDisabled: boolean;
  messages: ReturnType<typeof useChatMessages>["messages"];
  hasMore: boolean;
  isLoadingMore: boolean;
  exclusion: Exclusion | undefined;
  replyTo: ReplyContext | null;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  channel: GroupChannel | null | undefined;
  handleClose: () => void;
  handleSend: (message: string) => void;
  handleFileSelect: (file: File) => void;
  handleRetry: () => void;
  loadMore: () => void;
  setReplyTo: (context: ReplyContext) => void;
  clearReply: () => void;
}

export function useChatPanelState(params: UseChatPanelStateParams): UseChatPanelStateResult {
  const { modalState, workplaceId: workplaceIdFromCaller, workerId, placementId, onClose } = params;
  const session = useSession();
  const workplaceId = workplaceIdFromCaller ?? session.profile.userId;
  const isAdmin = Boolean(session.admin);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { replyTo, setReplyTo, clearReply } = useReplyState();

  const {
    data: blockedStatus,
    isLoading: isLoadingBlockedStatus,
    isError: isErrorBlockedStatus,
    refetch: refetchBlockedStatus,
  } = useWorkerBlockedStatus({ workplaceId, workerId });

  const { hasActiveChartingNoncompliance, isLoading: isLoadingChartingNoncompliance } =
    useWorkerHasActiveChartingNoncompliance({ workplaceId, workerId });

  const { mutate: ensureChannel } = useEnsureChatChannel();

  const {
    data: channel,
    isLoading: isLoadingChannel,
    isError: isErrorChannel,
    refetch: refetchChannel,
  } = useChatChannel({ workplaceId, workerId, placementId });

  const {
    messages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useChatMessages({ channel: channel ?? null });

  const { mutate: sendMessage, isLoading: isSending } = useSendTextMessage();
  const { mutate: sendFile, isLoading: isSendingFile } = useSendFileMessage();

  // Ensure channel exists when component mounts.
  // Only ensures the base workplaceId_workerId channel — placement channels
  // are created by the backend and don't need client-side creation.
  useEffect(() => {
    if (workplaceId && workerId) {
      ensureChannel({ workplaceId, workerId });
    }
  }, [ensureChannel, workplaceId, workerId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
    onClose?.();
  }, [modalState, onClose]);

  const handleSend = useCallback(
    (message: string) => {
      if (!channel) {
        return;
      }
      sendMessage(
        { channel, message, parentMessageId: replyTo?.messageId },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "text",
              isReply: Boolean(replyTo),
            });
            clearReply();
          },
        }
      );
    },
    [channel, sendMessage, replyTo, clearReply, workerId, workplaceId]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!channel) {
        return;
      }
      sendFile(
        { channel, file },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "file",
              isReply: false,
            });
          },
        }
      );
    },
    [channel, sendFile, workerId, workplaceId]
  );

  const isLoading =
    isLoadingChannel ||
    isLoadingMessages ||
    isLoadingBlockedStatus ||
    isLoadingChartingNoncompliance;
  const isError = isErrorChannel || isErrorMessages || isErrorBlockedStatus;
  const isBlocked = (blockedStatus?.isBlocked ?? false) && !hasActiveChartingNoncompliance;

  const handleRetry = useCallback(() => {
    void refetchChannel();
    void refetchBlockedStatus();
  }, [refetchChannel, refetchBlockedStatus]);

  return {
    workplaceId,
    isAdmin,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled: isSending || isSendingFile || !channel,
    messages,
    hasMore,
    isLoadingMore,
    exclusion: blockedStatus?.exclusion,
    replyTo,
    messagesEndRef,
    channel,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    loadMore,
    setReplyTo,
    clearReply,
  };
}
